import React from 'react';
import Head from 'next/head';
import qs from 'query-string';

import { BaseCardTileList, PageHero, QuizCardTile } from 'styled';
import { RouteMap } from 'constants/Routes';
import BrowsePagePagination from 'styled/components/BrowsePagePagination';

import type { Quiz, PageHero as IPageHero } from 'types';
import AlphabetNav from '../AlphabetNav';
import { useRouter } from 'next/router';
import Divider from '@mui/material/Divider';
import NotFound from '../svg/NotFound';
import { PageMeta } from 'components/Meta';

type Props = {
  pageNumber: number;
  totalPages: number;
  slug: string;
  quizzes: Quiz[];
  shouldPaginate: boolean;
  pageHero?: IPageHero;
  pageTitle: string;
};

const QuizCardTileList: React.FC<Props> = ({
  pageNumber,
  quizzes,
  slug,
  shouldPaginate,
  totalPages,
  pageHero,
  pageTitle,
}) => {
  const hasNextPage = pageNumber < totalPages;
  const hasPrevPage = pageNumber !== 1;
  const prevPage = pageNumber - 1;
  const { query, asPath, pathname } = useRouter();

  return (
    <div className="min-h-screen">
      <PageMeta
        title={pageTitle}
        description={pageHero?.description}
        image={pageHero?.desktopImage}
        pathname={asPath}
      />
      {shouldPaginate && totalPages > 1 && (
        <Head>
          {hasPrevPage && (
            <link
              rel="prev"
              href={`${pathname}?${qs.stringify({ page: prevPage === 1 ? undefined : prevPage, search: query.search })}`}
            />
          )}
          {hasNextPage && (
            <link
              rel="next"
              href={`${pathname}?${qs.stringify({ page: pageNumber + 1, search: query.search })}`}
            />
          )}
        </Head>
      )}

      {pageHero && (
        <div className="[&_img]:mb-6 [&_img]:mr-8 [&_img]:max-h-[100px] [&_img]:self-end sm:[&_img]:m-0 sm:[&_img]:max-h-[275px] sm:[&_img]:w-[50vw] sm:[&_img]:self-center md:[&_img]:max-h-[350px]">
          <PageHero hero={pageHero} />
        </div>
      )}

      <AlphabetNav
        value={(query.search as string)?.toUpperCase() || undefined}
        hrefBase={`${pathname}?search=`}
      />
      <Divider className="mb-4 mt-[-1px] sm:mb-12" />

      <div className="m-auto max-w-[90rem]">
        {quizzes.length ? (
          <>
            <BaseCardTileList>
              {quizzes.map((quiz) => (
                <QuizCardTile
                  key={quiz.id}
                  title={quiz.title?.replaceAll(' Quiz', '') || ''}
                  description=""
                  url={`${RouteMap.QUIZ.base}${quiz.slug}`}
                  medianDescriptionsLength={0}
                />
              ))}
            </BaseCardTileList>
            {totalPages > 1 && (
              <div className="my-16">
                <BrowsePagePagination
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                />
              </div>
            )}
          </>
        ) : (
          <div className="my-16 flex flex-col items-center justify-center text-center">
            <NotFound />
            <div className="font-primary-large mt-4">
              Oops, there&apos;s nothing here yet.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizCardTileList;
